import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { debounce } from 'utils/misc/debounce';
import { toggleDrawer } from 'store/ancillary/actions';
import { IconButton } from '@upmc/ims.component-library';
import ArrowBack from '@mui/icons-material/ArrowBack';
import { nanoid } from 'nanoid';
import { compareFn } from '../../../utils/objects/ObjectUtil';
import MenuListItem from './MenuListItem';
import { getMenuItems, getMenuFavorites } from '../_api';
import SidebarMenu from './SidebarMenu';
import { UserContext } from 'context/User/UserContext';
import {
	AppInfoIcon,
	AppsMenuIcon,
	FavoritesMenuIcon,
	HelpMenuIcon,
	HomeMenuIcon,
	RequestMenuIcon,
	SettingsMenuIcon,
	StaffMenuIcon,
	SupportMenuIcon,
	ToolsMenuIcon,
} from '../Icons/MenuIcons';

/**
 * Container component to manage primary menu.
 * @returns SidebarMenuContainer component
 */
const SidebarMenuContainer = ({ onClickShowFavorites }) => {
	const dispatch = useDispatch();
	const rDrawerOpen = useSelector(state => state.ancillary?.drawerOpen);

	const [isLoading, setIsLoading] = useState(true);
	const [recentItems, setRecentItems] = useState([]);
	const [menuItems, setMenuItems] = useState({});
	const [menuData, setMenuData] = useState([]);

	// track whether user has overridden default drawer states
	const refTouched = useRef(null);

	/**
	 * @type {import('context/User/UserContext').ContextUser}
	 */
	const contextUser = useContext(UserContext);
	const user =
		contextUser.userInfo.impersonationProfile ??
		contextUser.userInfo.userProfile;
	const otherItems = [
		{
			ID: -3,
			Name: 'App Info',
			Image: <AppInfoIcon />,
			ChildItems: [],
			ArgumentPage: '/ApplicationInfo',
			isReactPage: true,
		},
		{
			ID: -2,
			Name: 'Tools',
			Image: <ToolsMenuIcon />,
			ChildItems: [],
			ArgumentPage: '/AccountTools.aspx',
			LinkTarget: '_self',
		},
		{
			ID: -4,
			Name: 'Help',
			Image: <HelpMenuIcon />,
			ChildItems: [],
			ArgumentPage:
				'https://infonet.upmc.com/infonet/ls/content/922561812670663/ims-identity-management-system',
			LinkTarget: '_blank',
		},
	];
	const syntheticMenuItems = otherItems.reduce((accumulator, item) => {
		if (item.ChildItems?.length > 0) {
			accumulator[item.ID] = item.ChildItems.map(childItem => ({
				...childItem,
				ChildItems: [...childItem.ChildItems],
			}));
		}

		return accumulator;
	}, {});

	useEffect(() => {
		const handleResize = debounce(() => {
			if (refTouched.current !== true) {
				dispatch(toggleDrawer(window.innerWidth >= 1024));
			}
		}, 150);

		window.addEventListener('resize', handleResize);
		handleResize();

		return () => window.removeEventListener('resize', handleResize);
	}, [dispatch]);

	/**
	 * Effect ensuring drawer always displays root level when closed, particularly when closed programmatically.
	 */
	useEffect(() => {
		if (!rDrawerOpen) {
			setRecentItems([]);
		}
	}, [rDrawerOpen]);

	useEffect(() => {
		async function configure() {
			//get primary menu items
			let menuFavoritesID = -1;

			const menuItemResponse = await getMenuItems(user.ID);
			const tempMenuItems = {};
			menuItemResponse.data.forEach(item => {
				if (item.ParentID > 0 || item.ParentID === 0) {
					switch (item.Name.toLowerCase()) {
						case 'request':
							item.Image = <RequestMenuIcon />;
							item.ItemOrder = 1;
							break;
						case 'staff':
							item.Image = <StaffMenuIcon />;
							item.ItemOrder = 2;
							break;
						case 'apps':
							item.Image = <AppsMenuIcon />;
							item.ItemOrder = 3;
							break;
						case 'support':
							item.Image = <SupportMenuIcon />;
							item.ItemOrder = 4;
							break;
						case 'settings':
							item.Image = <SettingsMenuIcon />;
							item.ItemOrder = 5;
							break;
						case 'favorites':
							item.Image = <FavoritesMenuIcon />;
							menuFavoritesID = item.ID;
							item.ItemOrder = 6;
							break;
						case 'customize your favorites':
							item.ArgumentPage = null;
							item.CustomizeFavorites = true;
							item.OnClick = () => onClickShowFavorites();
							break;
						default:
							item.Image = null;
							item.ItemOrder = null;
							break;
					}
					if (!(item.ParentID in tempMenuItems)) {
						tempMenuItems[item.ParentID] = [];
					}
					tempMenuItems[item.ParentID].push(item);
				}
			});

			//get favorites
			const menuFavoritesResponse = await getMenuFavorites(user.ID);

			if (!(menuFavoritesID in tempMenuItems)) {
				tempMenuItems[menuFavoritesID] = [];
			}

			menuFavoritesResponse.MenuItems.forEach(item => {
				tempMenuItems[menuFavoritesID].push(item);
			});

			//set it all
			setMenuItems(tempMenuItems);
			setIsLoading(false);
		}
		if (user?.ID) {
			configure();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [user]);

	const onClickHandler = item => {
		if (item?.ChildItems?.length > 0) {
			const newRecentItems = [...recentItems];
			newRecentItems.push(item);
			setRecentItems(newRecentItems);
		}
	};

	const onBackClickHandler = () => {
		const newRecentItems = [...recentItems];
		newRecentItems.pop();
		setRecentItems(newRecentItems);
	};

	const onDrawerHandler = useCallback(
		(payload = null) => {
			refTouched.current = true;
			const state = typeof payload === 'boolean' ? payload : !rDrawerOpen;
			// preserve submenu selection when opening from collapsed state
			if (state !== rDrawerOpen && !state) {
				setRecentItems([]);
			}
			dispatch(toggleDrawer(state));
		},
		[rDrawerOpen, dispatch],
	);

	useEffect(() => {
		const tempMenuData = [];
		if (recentItems.length === 0) {
			tempMenuData.push([generateHome()]);
		}
		tempMenuData.push([generateBackLink()]);
		tempMenuData.push([generateMenuHeadings()]);

		if (recentItems.length === 0) {
			tempMenuData.push([generateOther()]);
			tempMenuData.push([generateFavorites()]);
		}

		setMenuData(tempMenuData);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isLoading, recentItems]);

	const generateBackLink = () => {
		const recentItem =
			recentItems?.length > 0
				? { ...recentItems[recentItems.length - 1] }
				: null;
		if (recentItem) {
			recentItem.ID = -1;
			recentItem.ArgumentPage = null;
			recentItem.LinkTarget = null;
			recentItem.isReactPage = false;
			recentItem.Image = (
				<IconButton
					component='span'
					onClick={onBackClickHandler}
					id={`arrow-back-${recentItem.ID}`}
				>
					<ArrowBack fontSize='inherit' />
				</IconButton>
			);
			recentItem.ChildItems = [];
			return (
				<MenuListItem
					key={'mli_back' + nanoid()}
					item={recentItem}
					onClick={onBackClickHandler}
				/>
			);
		} else {
			return null;
		}
	};

	const generateFavorites = () => {
		if (isLoading) {
			return generateLoadingTemplateItems(1);
		} else {
			let favoritesItem =
				menuItems && menuItems[0]
					? menuItems[0].find(
							i => i.Name.toLowerCase() === 'favorites',
						)
					: null;

			return favoritesItem ? (
				<MenuListItem
					key={'mli_fav' + favoritesItem.ID}
					item={favoritesItem}
					onClick={onClickHandler}
					isLoading={isLoading}
				/>
			) : null;
		}
	};

	const generateMenuHeadings = () => {
		if (isLoading) {
			return generateLoadingTemplateItems(5);
		} else {
			const currentMenuKey = recentItems[recentItems.length - 1]?.ID ?? 0;
			const currentMenuItems =
				syntheticMenuItems[currentMenuKey] ?? menuItems[currentMenuKey];

			return currentMenuItems
				?.sort((a, b) => {
					if (a.ItemOrder || b.ItemOrder) {
						return compareFn(a, b, 'ItemOrder');
					}
					if (a.IsParentItem && !b.IsParentItem) {
						return -1;
					}
					if (!a.IsParentItem && b.IsParentItem) {
						return 1;
					}
					return 0;
				})
				.map(item =>
					item.Name.toLowerCase() === 'favorites' ? null : (
						<MenuListItem
							key={'mli_' + item.ID}
							item={item}
							onClick={onClickHandler}
						/>
					),
				);
		}
	};

	const generateOther = () => {
		if (isLoading) {
			return generateLoadingTemplateItems(otherItems.length);
		} else {
			return otherItems.map(item =>
				item.Name.toLowerCase() === 'favorites' ? null : (
					<MenuListItem
						key={'mli_' + item.ID}
						item={{
							...item,
							ChildItems: item.ChildItems.map(childItem => ({
								...childItem,
								ChildItems: [...childItem.ChildItems],
							})),
						}}
						onClick={onClickHandler}
					/>
				),
			);
		}
	};

	const generateHome = () => {
		if (isLoading) {
			return generateLoadingTemplateItems(1);
		} else {
			return (
				<MenuListItem
					key={'mli_home'}
					item={{
						ID: -1,
						Name: 'Home',
						Image: <HomeMenuIcon />,
						ChildItems: [],
						ArgumentPage: '/index.html',
						LinkTarget: '',
					}}
					onClick={onClickHandler}
				/>
			);
		}
	};

	const generateLoadingTemplateItems = count => {
		return [...new Array(count)]
			.map((_, index) => [
				{
					ID: index,
					Name: 'TEMPLATE',
					Image: <AppInfoIcon />,
				},
			])
			.map(([item]) => (
				<MenuListItem
					key={'mli_placeholder_' + item.ID + '-' + nanoid()}
					item={item}
					isLoading={isLoading}
				/>
			));
	};

	return (
		<SidebarMenu
			menu={menuData}
			open={rDrawerOpen}
			onClick={onDrawerHandler}
		/>
	);
};

export default SidebarMenuContainer;
